import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { AuthService } from '@klickdata/core/auth';
import { LanguageService } from '@klickdata/core/localization';
import { Media, MediaData } from '@klickdata/core/media/src/media.model';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { S3MediaService } from '@klickdata/core/media/src/s3-media.service';
import { SideNaveActionsTypes, SideNaveData, SideNaveResponseData } from '@klickdata/core/mobile/src/mobile.service';
import { AppScope } from '@klickdata/core/resource';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-menu-side-add-image-to-list',
    templateUrl: './menu-side-add-image-to-list.component.html',
    styleUrls: ['./menu-side-add-image-to-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuSideAddImageToListComponent extends OnDestroyHandler implements OnInit {
    @Input() public navData: SideNaveData;
    @Output() onClose: EventEmitter<SideNaveResponseData> = new EventEmitter();
    public mediaForm: FormGroup;
    AppScope = AppScope;
    @Output() saving: EventEmitter<boolean> = new EventEmitter<boolean>();
    public currentLaguageId$: Observable<number>;
    public progress$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    public editedMedia: Media;

    constructor(
        protected fb: FormBuilder,
        protected mediaService: MediaService,
        protected langService: LanguageService,
        protected auth: AuthService,
        protected s3MediaService: S3MediaService,
        protected cd: ChangeDetectorRef
    ) {
        super();
        this.mediaForm = this.fb.group({
            name: [null],
            description: [''],
            visibility: ['private'],
            tag_ids: [],
            ids: this.fb.array([]),
        });
    }
    ngOnInit(): void {
        this.currentLaguageId$ = this.langService
            .getLanguageByKey(this.langService.getCurrentLanguage().value)
            .pipe(map((lang) => lang.id));
        if (this.navData.data.type === 'editImage' || this.navData.data.type === 'editVideo') {
            this.editedMedia = this.navData.data.media;
            this.mediaForm.patchValue({
                name: this.editedMedia.name ?? this.editedMedia.filename,
                description: this.editedMedia.description,
                visibility: this.editedMedia.visibility,
                tag_ids: this.editedMedia.tag_ids ?? this.editedMedia.tags?.map((tag) => tag.id),
            });
            this.idsArray.push(this.fb.control(this.editedMedia.id));
        }
    }

    get mediaType(): string {
        return this.navData?.data?.mediaType;
    }

    get idsArray(): FormArray {
        return this.mediaForm.get('ids') as FormArray;
    }
    public prepareFiles(event: Event) {
        const files = Array.from((event.target as HTMLInputElement).files) as File[];
        this.handleFiles(files);
    }
    public handleFiles(files: File[]): void {
        this.saving.emit(true);
        this.uploadFiles(files)
            .pipe(takeUntil(this.destroy))
            .subscribe(
                (mediaKeys) => {
                    this.saving.emit(false);
                    mediaKeys.forEach((media) => this.idsArray.push(this.fb.control(media.id)));
                    this.mediaForm
                        .get('name')
                        .patchValue(
                            !!this.mediaForm.value.name && this.mediaForm.value.name != ''
                                ? this.mediaForm.value.name
                                : mediaKeys[0].name ?? mediaKeys[0].filename
                        );
                    this.progress$.next(null);
                    this.submit(false);
                },
                (err) => {}
            );
    }

    private uploadFiles(files: File[]): Observable<Media[]> {
        const uploadObservables = files.map((file) =>
            this.s3MediaService.uploadMediaToS3(file, this.progress$).pipe(
                takeUntil(this.destroy),
                switchMap((media) =>
                    this.mediaService.uploadMediaByLink({
                        ...media.getData(),
                    })
                ),
                takeUntil(this.destroy)
            )
        );

        return forkJoin(uploadObservables);
    }

    public submit(withClose: boolean = true) {
        if (!this.mediaForm.value.ids.length) {
            return;
        }
        this.saving.emit(true);
        this.prepareData()
            .pipe(
                switchMap((data) =>
                    forkJoin(
                        data.map((item) => this.mediaService.updateMedia(+item.id, item, 'tags,author,user_feeds'))
                    )
                ),
                takeUntil(this.destroy)
            )
            .subscribe((medias) => {
                this.saving.emit(false);
                if (withClose) {
                    this.onClose.emit({
                        action: SideNaveActionsTypes.POSITIVE,
                        data: {
                            value: medias,
                            type: this.navData.data.type,
                            visibility: this.mediaForm.value.visibility,
                        },
                    });
                    this.mediaForm.reset();
                }
            });
    }
    prepareData(): Observable<MediaData[]> {
        const data: MediaData[] = this.idsArray.controls.map((control) => {
            return {
                id: control.value,
                visibility: this.mediaForm.value.visibility,
                tag_ids: this.mediaForm.value.tag_ids,
                name: this.mediaForm.value.name,
                description: this.mediaForm.value.description,
            };
        });
        return of(data);
    }
    public removeMedia(id: number) {
        this.mediaService
            .destroy(id)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                this.idsArray.removeAt(this.idsArray.controls.findIndex((item) => item.value === id));
                this.idsArray.markAsDirty();
                this.cd.markForCheck();
            });
    }
}
